// Phase JA.2 — pure helpers for the Daily Acca Signal Menu filter bar.
//
// All logic lives here so unit tests can exercise it without rendering.

import type { DailyAccaSignal, SignalTier } from "../../free-tier/useDailyAccaSignals.ts";

export type TierFilter = "all" | SignalTier;

/** Coarse market category buckets for the filter pill row. */
export type MarketCategory = "all" | "1x2" | "goals" | "btts" | "cards" | "corners";

export interface SignalMenuFilters {
  tier: TierFilter;
  category: MarketCategory;
  league: string; // exact match, "all" sentinel for no filter.
  minConviction: number; // 0–100, inclusive lower bound.
}

export const DEFAULT_FILTERS: SignalMenuFilters = {
  tier: "all",
  category: "all",
  league: "all",
  minConviction: 0,
};

/** Map a signal's `market` text to a coarse filter category. Best-effort. */
export function classifyMarket(market: string): Exclude<MarketCategory, "all"> | null {
  const m = market.toLowerCase();
  if (m.includes("corner")) return "corners";
  if (m.includes("card") || m.includes("booking")) return "cards";
  if (m.includes("btts") || m.includes("both teams")) return "btts";
  if (m.includes("goal") || m.includes("over") || m.includes("under") || m.includes("total")) {
    return "goals";
  }
  if (m.includes("1x2") || m.includes("match result") || m.includes("win") || m.includes("draw")) {
    return "1x2";
  }
  return null;
}

export function applyFilters(
  signals: readonly DailyAccaSignal[],
  filters: SignalMenuFilters,
): DailyAccaSignal[] {
  return signals.filter((s) => {
    if (filters.tier !== "all" && s.tier !== filters.tier) return false;
    if (filters.category !== "all") {
      const cat = classifyMarket(s.market);
      if (cat !== filters.category) return false;
    }
    if (filters.league !== "all" && s.league !== filters.league) return false;
    if (filters.minConviction > 0) {
      const c = s.conviction_score ?? 0;
      if (c < filters.minConviction) return false;
    }
    return true;
  });
}

/** Top-N leagues by frequency in the signal set. */
export function topLeagues(signals: readonly DailyAccaSignal[], n = 8): string[] {
  const counts = new Map<string, number>();
  for (const s of signals) {
    if (!s.league) continue;
    counts.set(s.league, (counts.get(s.league) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, n)
    .map(([k]) => k);
}

/** Tier label for badges. */
export function tierLabel(tier: SignalTier): string {
  if (tier === "tier_a") return "TIER A · HIGH";
  if (tier === "tier_b") return "TIER B · SOLID";
  return "TIER C · VALUE";
}

/** Map conviction_score → 1..5 dot count (per spec §7). */
export function convictionToDots(conviction: number | null | undefined): number {
  if (conviction === null || conviction === undefined || !Number.isFinite(conviction)) return 0;
  if (conviction >= 85) return 5;
  if (conviction >= 75) return 4;
  if (conviction >= 65) return 3;
  if (conviction >= 50) return 2;
  if (conviction > 0) return 1;
  return 0;
}

/** Next 09:00 Africa/Lagos (WAT, UTC+1) publication target as ISO. */
export function nextPublishISO(now: Date = new Date()): string {
  // WAT is UTC+1 with no DST. 09:00 WAT = 08:00 UTC.
  const target = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 8, 0, 0, 0),
  );
  if (target.getTime() <= now.getTime()) {
    target.setUTCDate(target.getUTCDate() + 1);
  }
  return target.toISOString();
}

/** URL-param serialisation. Only emits non-default values. */
export function filtersToParams(filters: SignalMenuFilters): Record<string, string> {
  const out: Record<string, string> = {};
  if (filters.tier !== DEFAULT_FILTERS.tier) out["tier"] = filters.tier;
  if (filters.category !== DEFAULT_FILTERS.category) out["cat"] = filters.category;
  if (filters.league !== DEFAULT_FILTERS.league) out["lg"] = filters.league;
  if (filters.minConviction !== DEFAULT_FILTERS.minConviction) {
    out["minc"] = String(filters.minConviction);
  }
  return out;
}

const VALID_TIERS: TierFilter[] = ["all", "tier_a", "tier_b", "tier_c"];
const VALID_CATS: MarketCategory[] = ["all", "1x2", "goals", "btts", "cards", "corners"];

export function filtersFromParams(get: (k: string) => string | null): SignalMenuFilters {
  const tier = get("tier");
  const cat = get("cat");
  const lg = get("lg");
  const minc = get("minc");
  const minN = minc !== null ? Number(minc) : NaN;
  return {
    tier: tier && VALID_TIERS.includes(tier as TierFilter) ? (tier as TierFilter) : "all",
    category: cat && VALID_CATS.includes(cat as MarketCategory) ? (cat as MarketCategory) : "all",
    league: lg && lg.length > 0 ? lg : "all",
    minConviction: Number.isFinite(minN) ? Math.max(0, Math.min(100, Math.round(minN))) : 0,
  };
}
